import React, { useMemo, useState } from "react";
import { isEmpty, chain, map } from "ramda";
import debounce from "lodash.debounce";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import { HIDE_VIEWER } from "media-viewer/mediaViewerRedux";
import { SCROLL_DIRECTION_VERTICAL } from "biblioboard-pdf-viewer";
import AppBar from "@material-ui/core/AppBar";
import CircularProgress from "@material-ui/core/CircularProgress";
import Close from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import PdfViewer from "media-viewer/PdfViewer";
import Popover from "@material-ui/core/Popover";
import SearchIcon from "@material-ui/icons/Search";
import Slider from "@material-ui/core/Slider";
import TocIcon from "@material-ui/icons/Toc";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import SearchDialog from "media-viewer/SearchDialog";
import TocDialog from "media-viewer/TocDialog";

const useStyles = makeStyles(theme => ({
  title: {
    textAlign: "center",
    flex: 1,
    color: theme.palette.text.secondary,
  },
  appBar: {
    backgroundColor: "white",
    boxShadow: "none",
  },
  appBarBottom: {
    top: "auto",
    bottom: 0,
    backgroundColor: "white",
    boxShadow: "none",
  },
  viewerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    position: "relative",
    backgroundColor: theme.palette.grey[100],
    flex: 1,
  },
  zoomMenuPaper: {
    height: 300,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  zoomSlider: {
    flex: 1,
  },
  zoomSliderIcon: {
    margin: "0px 40px 0px 20px",
    height: 60,
  },
  slider: {
    margin: "0 20px",
  },
  sliderTrack: {
    color: theme.palette.text.secondary,
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },
  sliderRail: {
    color: theme.palette.grey[800],
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },
  sliderThumb: {
    backgroundColor: theme.palette.grey["A400"],
    "&:hover": {
      boxShadow: "0px 0px 0px 8px rgba(0, 0, 0, .1)",
    },
    "&:active": {
      boxShadow: "0px 0px 0px 14px rgba(0, 0, 0, .1)",
    },
    ".Mui-disabled &": {
      backgroundColor: theme.palette.grey[400],
    },
  },
  sliderMarkLabel: {
    fontSize: "1.4rem",
    fontWeight: 700,
  },
  sliderValueLabel: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "black",
  },
}));

const spreadMargin = {
  top: 10,
  right: 10,
  bottom: 10,
  left: 10,
};

const zoomMarks = [
  {
    value: 4,
    label: "4x",
  },
  {
    value: 2,
    label: "2x",
  },
  {
    value: 0,
    label: "1x",
  },
  {
    value: -2,
    label: ".5x",
  },
  {
    value: -4,
    label: ".25x",
  },
];

const PdfViewerDialog = () => {
  const classes = useStyles();

  const { media } = useSelector(state => state.mediaViewer);
  const dispatch = useDispatch();

  const onClose = () => dispatch({ type: HIDE_VIEWER });

  const url = media.pdfUrl.replace("content.pdf", "content_optimized.pdf");

  const [zoom, setZoom] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [requestedPageNumber, setRequestedPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [pagesPerSpread] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [zoomMenuAnchor, setZoomMenuAnchor] = useState(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const [toc, setToc] = useState([]);

  const debouncedUpdatePageNumber = useMemo(
    () =>
      debounce(pageNumber => {
        setPageNumber(pageNumber);
      }, 400),
    [],
  );

  const debouncedUpdateRequestedPageNumber = useMemo(
    () =>
      debounce(pageNumber => {
        setRequestedPageNumber(pageNumber);
      }, 100),
    [],
  );

  const handleSearch = query => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchResults([]);
    setSearchQuery("");
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
  };

  const handleSearchItemClick = item => {
    setPageNumber(parseInt(item.pageIndex, 10) + 1);
    setSearchDialogOpen(false);
  };

  const handleTocDialogClose = () => {
    setTocDialogOpen(false);
  };

  const handleTocItemClick = item => {
    setPageNumber(parseInt(item.pageIndex, 10) + 1);
    setTocDialogOpen(false);
  };

  return (
    <Dialog
      fullScreen={true}
      onClose={onClose}
      aria-label="pdf viewer"
      open={true}
    >
      <AppBar className={classes.appBar} position="relative">
        <Toolbar variant="dense">
          <Typography variant="h5" className={classes.title}>
            {media.title}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>
      <div className={classes.viewerContainer}>
        <PdfViewer
          url={url}
          scrollDirection={SCROLL_DIRECTION_VERTICAL}
          pagesPerSpread={pagesPerSpread}
          spreadMargin={spreadMargin}
          gestures={false}
          pageNumber={pageNumber}
          zoom={zoom}
          searchQuery={searchQuery}
          onPageChange={e => {
            const { pageNumber } = e;
            setPageNumber(pageNumber);
            debouncedUpdateRequestedPageNumber(pageNumber);
          }}
          onZoomChange={e => {
            setZoom(e.zoom);
          }}
          onPdfLoaded={e => {
            setPageCount(e.pageCount);
            setPdfLoaded(true);
          }}
          onSearchMatches={e => {
            const { results } = e;

            const mappedResults = chain(result => {
              const { pageIndex, matches } = result;

              return map(
                match => ({
                  pageIndex,
                  excerpt: match,
                }),
                matches,
              );
            }, results);

            setSearchResults(mappedResults);
          }}
          onTocLoaded={toc => {
            const mappedToc = map(
              ({ title, index }) => ({
                pageIndex: index,
                label: title,
              }),
              toc,
            );

            setToc(mappedToc);
          }}
        />
        {!pdfLoaded && (
          <CircularProgress
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginLeft: "-20px",
              marginTop: "-20px",
            }}
          />
        )}
      </div>
      <AppBar position="relative" className={classes.appBarBottom}>
        <Toolbar variant="dense">
          <IconButton
            disabled={isEmpty(toc)}
            aria-label="table of contents"
            onClick={() => {
              setTocDialogOpen(true);
            }}
          >
            <TocIcon />
          </IconButton>
          <IconButton
            aria-label="search"
            onClick={() => {
              setSearchDialogOpen(true);
            }}
          >
            <SearchIcon />
          </IconButton>
          <IconButton
            aria-label="zoom"
            onClick={event => {
              setZoomMenuAnchor(event.currentTarget);
            }}
          >
            <ZoomInIcon />
          </IconButton>
          <Popover
            anchorEl={zoomMenuAnchor}
            open={Boolean(zoomMenuAnchor)}
            onClose={() => {
              setZoomMenuAnchor(null);
            }}
            classes={{
              paper: classes.zoomMenuPaper,
            }}
            PaperProps={{
              onMouseLeave: () => {
                setZoomMenuAnchor(null);
              },
            }}
          >
            <ZoomInIcon className={classes.zoomSliderIcon} />
            <Slider
              track={false}
              className={classes.zoomSlider}
              orientation="vertical"
              marks={zoomMarks}
              max={4}
              min={-4}
              value={Math.log2(zoom) * 2}
              aria-labelledby="zoom slider"
              onChange={(e, value) => {
                setZoom(Math.pow(2, value / 2));
              }}
              classes={{
                rail: classes.sliderRail,
                track: classes.sliderTrack,
                thumb: classes.sliderThumb,
                markLabel: classes.sliderMarkLabel,
              }}
            />
            <ZoomOutIcon className={classes.zoomSliderIcon} />
          </Popover>
          <Slider
            min={1}
            max={pageCount}
            // marks={true}
            valueLabelDisplay="auto"
            disabled={!pdfLoaded}
            className={classes.slider}
            value={requestedPageNumber}
            onChange={(e, value) => {
              setRequestedPageNumber(value);
              debouncedUpdatePageNumber(value);
            }}
            classes={{
              rail: classes.sliderRail,
              track: classes.sliderTrack,
              thumb: classes.sliderThumb,
              valueLabel: classes.sliderValueLabel,
            }}
          />
        </Toolbar>
      </AppBar>
      <TocDialog
        toc={toc}
        open={tocDialogOpen}
        onClose={handleTocDialogClose}
        onItemClick={handleTocItemClick}
      />
      <SearchDialog
        open={searchDialogOpen}
        searchResults={searchResults}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        onClose={handleSearchDialogClose}
        onItemClick={handleSearchItemClick}
      />
    </Dialog>
  );
};

export default PdfViewerDialog;
